import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { widgetUsedFields, autoFieldMap } from "@/lib/builderOps";
import { Database, ArrowRight, AlertTriangle } from "lucide-react";

// Shown when pasted widgets reference datasets not available in this workspace.
// missing: [{dataset_id, name, schema, widgets: [widget,...]}]
// onConfirm(mappings): {dataset_id: {dest_dataset_id, fieldMap}}
export function DatasetRemapDialog({ open, onOpenChange, missing, datasets, onConfirm }) {
  const [sel, setSel] = useState({});        // src dataset_id -> dest dataset id
  const [fieldMaps, setFieldMaps] = useState({}); // src dataset_id -> {srcField: destField}

  useEffect(() => {
    if (!open) return;
    const s = {}, fm = {};
    (missing || []).forEach((m) => {
      const dest = datasets[0];
      s[m.dataset_id] = dest?.id || "";
      const fields = [...new Set(m.widgets.flatMap(widgetUsedFields))];
      fm[m.dataset_id] = dest ? autoFieldMap(fields, dest.schema) : {};
    });
    setSel(s); setFieldMaps(fm);
  }, [open, missing, datasets]);

  const pickDest = (srcId, destId) => {
    const dest = datasets.find((d) => d.id === destId);
    const m = missing.find((x) => x.dataset_id === srcId);
    const fields = [...new Set(m.widgets.flatMap(widgetUsedFields))];
    setSel((v) => ({ ...v, [srcId]: destId }));
    setFieldMaps((v) => ({ ...v, [srcId]: autoFieldMap(fields, dest?.schema || []) }));
  };
  const setField = (srcId, from, to) => setFieldMaps((v) => ({ ...v, [srcId]: { ...v[srcId], [from]: to } }));

  const complete = useMemo(() => (missing || []).every((m) =>
    sel[m.dataset_id] && Object.values(fieldMaps[m.dataset_id] || {}).every((v) => v)
  ), [missing, sel, fieldMaps]);

  const confirm = () => {
    const out = {};
    (missing || []).forEach((m) => { out[m.dataset_id] = { dest_dataset_id: sel[m.dataset_id], fieldMap: fieldMaps[m.dataset_id] || {} }; });
    onConfirm(out);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="dataset-remap-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Database className="w-5 h-5 text-[color:var(--brand-ocean)]" />Map datasets for pasted widgets</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-[color:var(--ink-3)] flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[color:var(--brand-mint)]" />
          The copied widgets use datasets that aren't available here. Pick a replacement dataset and map each field before inserting.
        </p>
        <div className="space-y-5">
          {(missing || []).map((m) => {
            const dest = datasets.find((d) => d.id === sel[m.dataset_id]);
            const fields = [...new Set(m.widgets.flatMap(widgetUsedFields))];
            return (
              <div key={m.dataset_id} className="rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-3 space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-[color:var(--ink)]">{m.name}</span>
                  <span className="text-[10px] text-[color:var(--ink-3)]">({m.widgets.length} widget{m.widgets.length > 1 ? "s" : ""})</span>
                  <ArrowRight className="w-3.5 h-3.5 text-[color:var(--ink-3)]" />
                  <Select value={sel[m.dataset_id] || ""} onValueChange={(v) => pickDest(m.dataset_id, v)}>
                    <SelectTrigger data-testid="remap-dest-dataset" className="h-8 w-[210px] bg-[color:var(--surface)]"><SelectValue placeholder="Pick destination dataset" /></SelectTrigger>
                    <SelectContent>{datasets.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {dest && fields.length > 0 && (
                  <div className="space-y-1.5">
                    <Label className="text-[11px] text-[color:var(--ink-3)]">Field mapping</Label>
                    {fields.map((f) => (
                      <div key={f} className="flex items-center gap-2">
                        <span className="text-xs text-[color:var(--ink-2)] w-[38%] truncate font-mono">{f}</span>
                        <ArrowRight className="w-3 h-3 shrink-0 text-[color:var(--ink-3)]" />
                        <Select value={fieldMaps[m.dataset_id]?.[f] || ""} onValueChange={(v) => setField(m.dataset_id, f, v)}>
                          <SelectTrigger data-testid={`remap-field-${f}`} className="h-8 flex-1 min-w-0 bg-[color:var(--surface)]"><SelectValue placeholder="Pick field" /></SelectTrigger>
                          <SelectContent>{(dest.schema || []).map((c) => <SelectItem key={c.name} value={c.name}>{c.name} <span className="text-[10px] text-[color:var(--ink-3)]">· {c.type}</span></SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="remap-confirm-btn" disabled={!complete} onClick={confirm} className="bg-[color:var(--brand-ocean)] text-white">Insert widgets</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
